import { EventName, EventNameSource } from '@/shared/event-types';
import OurModal from '@/components/Modal';
import { useState } from 'react';
import Step1, { PLATFORMS } from './Step1';
import Step2, { AccountInfo } from './Step2';
import { postSeachSocialAccountApi, getAccountListApi, postAccountAddApi } from '@/lib/service';
import { useUserProvider } from '@/context/UserProvider';
import { useRouter } from 'next/navigation';
import { ga_logEvent } from '@/shared/ga';
import { message } from 'antd';

interface Props {
  open: boolean;
  onCancel: () => void;
  pageSource: EventNameSource;
  /** 初始平台，如果指定则直接跳到第二步 */
  initialPlatform?: string;
  /** 默认选中的平台，停留在第一步并高亮该平台，用户仍可改选 */
  defaultPlatform?: string;
  /** 文案变体，'shadowban' 时使用检测泄露与假账号的专属文案 */
  copyVariant?: 'default' | 'shadowban';
  /** 添加成功后的回调函数 */
  onSuccess?: () => void;
}

// 校验辅助函数
// const verifyDescFunc = (description: any) => {
//   if (description) {
//     if (description.indexOf('erasa') == -1) {
//       return 2;
//     } else {
//       return 1;
//     }
//   } else {
//     return 2;
//   }
// };

function AddAccountNewModal({
  open,
  onCancel,
  pageSource,
  initialPlatform,
  defaultPlatform,
  copyVariant = 'default',
  onSuccess,
}: Props) {
  const { isLogin, userSub, onOpenLoginModal } = useUserProvider();
  const router = useRouter();
  // 当前步骤：如果有 initialPlatform 则直接从第二步开始
  // defaultPlatform 只做预选，仍停留在第一步
  const [step, setStep] = useState(initialPlatform ? 2 : 1);
  // 当前选中的平台：initialPlatform 优先，其次是 defaultPlatform 的预选值
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(
    initialPlatform ?? defaultPlatform ?? null,
  );
  // 升级提示相关状态
  const [showUpgradeDesc, setShowUpgradeDesc] = useState(false);
  const [showUpgradeDescLoading, setShowUpgradeDescLoading] = useState(false);
  // 添加账号 loading 状态
  const [addAccountLoading, setAddAccountLoading] = useState(false);

  // 处理平台点击事件
  const handlePlatformClick = (platformKey: string) => {
    setSelectedPlatform(platformKey);
    setStep(2);
  };

  // 处理返回事件
  const handleBack = () => {
    setStep(1);
  };

  // 处理关闭事件
  const handleClose = () => {
    setSelectedPlatform(null);
    setStep(1);
    setShowUpgradeDesc(false);
    setShowUpgradeDescLoading(false);
    onCancel();
  };

  // 判断用户订阅是否到期
  const isSubscriptionExpired = () => {
    if (!userSub?.item_id) {
      // 如果没有订阅，视为到期
      return true;
    }
    // 检查状态是否为过期
    if (userSub?.status === 'expire') {
      return true;
    }
    // 检查过期时间是否小于当前时间
    if (userSub?.expire_time) {
      const expireTime = new Date(userSub.expire_time).getTime();
      const currentTime = new Date().getTime();
      return expireTime < currentTime;
    }
    // 如果状态是 normal 或 active，视为未到期
    if (userSub?.status === 'normal' || userSub?.status === 'active') {
      return false;
    }
    // 其他情况视为到期
    return true;
  };

  // 实际调用添加账号接口
  const doAddAccount = (accountInfo: AccountInfo) => {
    setAddAccountLoading(true);
    postAccountAddApi({
      url: accountInfo.url,
      name: accountInfo.name,
      username: accountInfo.username,
      avatar: accountInfo.avatar_url,
      platform: accountInfo.social_type,
      description: accountInfo.profile_description,
      verify_status: 0,
    })
      .then((rs) => {
        if (rs.code == 0) {
          ga_logEvent(EventName.account_add_success, { page_source: pageSource });
          message.success(rs.message);
          // 判断 onSuccess 是否存在，如果存在则调用
          if (onSuccess) {
            onSuccess();
          } else {
            router.push('/dashboard');
          }
          handleClose();
        } else {
          message.warning(rs.message);
        }
      })
      .finally(() => {
        setAddAccountLoading(false);
      });
  };

  // 处理确认添加账号
  const handleConfirmAdd = (accountInfo: AccountInfo) => {
    // 判断是否登录
    if (!isLogin) {
      onOpenLoginModal(true);
      return;
    }

    if (showUpgradeDescLoading || addAccountLoading) return;

    // 免费用户（未订阅或试用中）需要检查账号数量
    const isFreeUser = !userSub?.item_id || userSub?.status == 'trialing';

    if (isFreeUser || isSubscriptionExpired()) {
      setShowUpgradeDescLoading(true);
      getAccountListApi()
        .then((res) => {
          if (res.code == 0 && res.data.length >= 1) {
            // 免费用户已有账号，显示升级提示，不调用添加接口
            setShowUpgradeDesc(true);
          } else {
            // 免费用户还没有账号，允许添加
            doAddAccount(accountInfo);
          }
        })
        .finally(() => {
          setShowUpgradeDescLoading(false);
        });
    } else {
      // 付费用户直接添加，无限制
      doAddAccount(accountInfo);
    }
  };

  // 处理提交事件 - 调用添加账号接口
  const handleSubmit = async (username: string): Promise<{ success: boolean; data?: { name?: string; handle?: string; avatar?: string } }> => {
    // TODO: 替换为实际的 API 调用
    // 模拟 API 调用
    return new Promise((resolve) => {
      // 判断输入的是用户名还是 url
      const isUrl = /^https?:\/\//i.test(username);

      let finalUrl: string;
      let finalSocialType: string;
      let finalUsername: string;

      if (isUrl) {
        // 如果是 URL，从 URL 中提取平台和用户名
        const normalizeHostname = (host: string) => host.replace(/^www\./i, '').toLowerCase();
        const inputHostname = (() => {
          try {
            return normalizeHostname(new URL(username).hostname);
          } catch {
            return '';
          }
        })();
        const matchedPlatform = PLATFORMS.find((p) => {
          if (!p.url) return false;
          try {
            return normalizeHostname(new URL(p.url).hostname) === inputHostname;
          } catch {
            return false;
          }
        });
        // 从 URL 中提取用户名（取路径的第一段，去掉 @ 前缀）
        try {
          const urlObj = new URL(username);
          const pathParts = urlObj.pathname.split('/').filter(Boolean);
          finalUsername = pathParts[0]?.replace(/^@/, '') || '';
        } catch {
          finalUsername = '';
        }

        if (matchedPlatform) {
          // 找到匹配的平台，切换到该平台
          setSelectedPlatform(matchedPlatform.key);
          finalSocialType = matchedPlatform.name;
        } else {
          // 没找到匹配的平台，切换到 OTHER
          setSelectedPlatform('OTHER');
          finalSocialType = 'Other';
        }
        finalUrl = username;
      } else {
        // 如果是用户名，拼接平台 URL；统一去掉可能输入的前缀 @
        const platform = PLATFORMS.find((p) => p.key === selectedPlatform);
        const sanitizedUsername = username.replace(/^@+/, '');
        finalUsername = sanitizedUsername;
        finalUrl = (platform?.url || '') + sanitizedUsername;
        finalSocialType = platform?.name || selectedPlatform || '';
      }
      postSeachSocialAccountApi({
        username: finalUsername,
        social_type: finalSocialType,
        url: finalUrl,
      }).then((res) => {
        if (res.code == 0) {
          resolve({
            success: true,
            data: res.data,
          });
        } else {
          resolve({ success: false });
        }
      });
    });
  };

  return (
    <OurModal
      isModalOpen={open}
      handleCancel={handleClose}
      width={step === 1 ? 652 : 512}
      isCenter
      isMask
      closeIcon={false}
      isNoAddAccount={false}
      title={null}
      closable={false}
    >
      <div>
        {step === 1 && (
          <Step1
            selectedPlatform={selectedPlatform}
            onPlatformSelect={handlePlatformClick}
            onClose={handleClose}
            copyVariant={copyVariant}
          />
        )}
        {step === 2 && selectedPlatform && (
          <Step2
            platform={selectedPlatform}
            onBack={handleBack}
            onClose={handleClose}
            onSubmit={handleSubmit}
            onConfirmAdd={handleConfirmAdd}
            showUpgradeDesc={showUpgradeDesc}
            showUpgradeDescLoading={showUpgradeDescLoading}
            setShowUpgradeDesc={setShowUpgradeDesc}
            addAccountLoading={addAccountLoading}
            copyVariant={copyVariant}
          />
        )}
      </div>
    </OurModal>
  );
}

export default AddAccountNewModal;
